// Native APNs/FCM registration helpers.
//
// This is intentionally separate from lib/push.js: Capacitor returns an APNs
// token on iOS and an FCM token on Android, neither of which is a browser
// PushSubscription. The backend confirms platform readiness before the app
// asks for permission or stores a token.

import { Capacitor } from "@capacitor/core";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const REGISTRATION_TIMEOUT_MS = 12000;
let refreshInFlight = null;

function nativePlatform() {
  if (!Capacitor.isNativePlatform()) return null;
  const platform = Capacitor.getPlatform();
  return platform === "ios" || platform === "android" ? platform : null;
}

export function isNativePushPlatform() {
  return Boolean(nativePlatform());
}

function tokenKey(platform) {
  return `honeyacorn.native-push-token.${platform}.v1`;
}

function unavailableState(reason, error = null) {
  return {
    supported: false,
    permission: "unsupported",
    subscribed: false,
    endpoint: null,
    native: true,
    reason,
    error,
  };
}

async function nativePlugins() {
  const [{ PushNotifications }, { Preferences }] = await Promise.all([
    import("@capacitor/push-notifications"),
    import("@capacitor/preferences"),
  ]);
  return { PushNotifications, Preferences };
}

async function authenticatedRequest(path, { method = "GET", body } = {}) {
  if (!isSupabaseConfigured) {
    throw new Error("Push notifications require a signed-in HoneyAcorn account.");
  }
  const { data, error } = await supabase.auth.getSession();
  const accessToken = data?.session?.access_token;
  if (error || !accessToken) throw new Error("Sign in again to manage push notifications.");

  const response = await fetch(`${API_BASE}/api/notifications/native-push${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }
  if (!response.ok) {
    const detail = typeof payload?.detail === "string" ? payload.detail : null;
    const failure = new Error(detail || `Push setup failed (${response.status})`);
    failure.status = response.status;
    throw failure;
  }
  return payload;
}

async function registeredToken(Preferences, platform) {
  const { value } = await Preferences.get({ key: tokenKey(platform) });
  return value || null;
}

export async function getNativePushState() {
  const platform = nativePlatform();
  if (!platform) return unavailableState("not_native");

  let status;
  try {
    status = await authenticatedRequest("/status");
  } catch (error) {
    return unavailableState("status_unavailable", error?.message || String(error));
  }
  if (!status?.platforms?.[platform]) return unavailableState("not_configured");

  try {
    const { PushNotifications, Preferences } = await nativePlugins();
    const permissionStatus = await PushNotifications.checkPermissions();
    const token = await registeredToken(Preferences, platform);
    return {
      supported: true,
      permission: permissionStatus?.receive || "default",
      subscribed: permissionStatus?.receive === "granted" && Boolean(token),
      endpoint: null,
      native: true,
      reason: null,
      error: null,
    };
  } catch (error) {
    return unavailableState("plugin_unavailable", error?.message || String(error));
  }
}

async function ensureAndroidChannel(PushNotifications, platform) {
  if (platform !== "android") return;
  await PushNotifications.createChannel({
    id: "honeyacorn_messages",
    name: "Messages and updates",
    description: "Messages and important center updates from HoneyAcorn",
    importance: 4,
    visibility: 1,
    vibration: true,
  });
}

async function registerAndWaitForToken(PushNotifications) {
  let registrationHandle;
  let errorHandle;
  let timeoutId;
  try {
    const tokenPromise = new Promise((resolve, reject) => {
      timeoutId = window.setTimeout(
        () => reject(new Error("Push registration took too long. Please try again.")),
        REGISTRATION_TIMEOUT_MS,
      );
      Promise.all([
        PushNotifications.addListener("registration", (token) => resolve(token?.value || "")),
        PushNotifications.addListener("registrationError", (error) => {
          reject(new Error(error?.error || "Push registration failed."));
        }),
      ])
        .then(([success, failure]) => {
          registrationHandle = success;
          errorHandle = failure;
          return PushNotifications.register();
        })
        .catch(reject);
    });
    const token = await tokenPromise;
    if (typeof token !== "string" || token.length < 16) {
      throw new Error("Push registration did not return a usable device token.");
    }
    return token;
  } finally {
    if (timeoutId) window.clearTimeout(timeoutId);
    await registrationHandle?.remove?.();
    await errorHandle?.remove?.();
  }
}

export async function subscribeToNativePush() {
  const platform = nativePlatform();
  if (!platform) return getNativePushState();

  const initial = await getNativePushState();
  if (!initial.supported) {
    if (initial.reason === "not_configured") {
      throw new Error("Push notifications are not configured for this HoneyAcorn app build.");
    }
    throw new Error(initial.error || "HoneyAcorn could not check native push setup. Try again later.");
  }

  const { PushNotifications, Preferences } = await nativePlugins();
  let permissionStatus = await PushNotifications.checkPermissions();
  if (permissionStatus.receive === "prompt" || permissionStatus.receive === "prompt-with-rationale") {
    permissionStatus = await PushNotifications.requestPermissions();
  }
  if (permissionStatus.receive !== "granted") {
    const error = new Error("Notifications are blocked in this app's device settings.");
    error.code = "denied";
    throw error;
  }

  await ensureAndroidChannel(PushNotifications, platform);
  const token = await registerAndWaitForToken(PushNotifications);
  await authenticatedRequest("/register", {
    method: "POST",
    body: { platform, token },
  });
  try {
    await Preferences.set({ key: tokenKey(platform), value: token });
  } catch (error) {
    // Keep the backend association and the locally displayed state in sync.
    // If durable local storage is unavailable, best-effort rollback avoids a
    // device continuing to receive alerts while the control appears off.
    try {
      await authenticatedRequest("/unregister", {
        method: "POST",
        body: { platform, token },
      });
    } catch {
      // The provider's stale-token cleanup remains the fallback if offline.
    }
    try {
      await PushNotifications.unregister();
    } catch {
      // Registration may already be invalidated at the OS level.
    }
    throw new Error(error?.message || "Could not save the native push preference.");
  }
  return getNativePushState();
}

async function refreshNativePushRegistrationInternal() {
  const platform = nativePlatform();
  if (!platform) return getNativePushState();

  // Do not prompt or silently opt a user in. A durable local token proves
  // that this installed app was explicitly enabled before we refresh it.
  const initial = await getNativePushState();
  if (
    !initial.supported
    || initial.permission !== "granted"
    || !initial.subscribed
  ) {
    return initial;
  }

  const { PushNotifications, Preferences } = await nativePlugins();
  const previousToken = await registeredToken(Preferences, platform);
  if (!previousToken) return initial;

  await ensureAndroidChannel(PushNotifications, platform);
  const refreshedToken = await registerAndWaitForToken(PushNotifications);
  await authenticatedRequest("/register", {
    method: "POST",
    body: { platform, token: refreshedToken },
  });

  try {
    await Preferences.set({ key: tokenKey(platform), value: refreshedToken });
  } catch (error) {
    // Retain the existing association if the replacement token cannot be
    // remembered locally. A later app launch can recover without silently
    // changing the state shown to the family or teacher.
    if (refreshedToken !== previousToken) {
      try {
        await authenticatedRequest("/unregister", {
          method: "POST",
          body: { platform, token: refreshedToken },
        });
      } catch {
        // Provider stale-token cleanup remains the final safety net.
      }
    }
    throw new Error(error?.message || "Could not refresh the native push registration.");
  }

  if (refreshedToken !== previousToken) {
    try {
      await authenticatedRequest("/unregister", {
        method: "POST",
        body: { platform, token: previousToken },
      });
    } catch {
      // The new device token is already registered. Leaving the old token for
      // provider-directed stale cleanup avoids turning a temporary network
      // outage into a loss of notifications.
    }
  }
  return getNativePushState();
}

export function refreshNativePushRegistration() {
  if (!refreshInFlight) {
    refreshInFlight = refreshNativePushRegistrationInternal().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

export async function unsubscribeFromNativePush() {
  const platform = nativePlatform();
  if (!platform) return getNativePushState();

  let PushNotifications;
  let Preferences;
  try {
    ({ PushNotifications, Preferences } = await nativePlugins());
    const token = await registeredToken(Preferences, platform);
    if (token) {
      try {
        await authenticatedRequest("/unregister", {
          method: "POST",
          body: { platform, token },
        });
      } catch {
        // The device-level unregister still prevents future local delivery;
        // an unreachable backend will remove its stale record after a provider
        // response rather than keeping a user trapped in the enabled state.
      }
      try {
        await PushNotifications.unregister();
      } catch {
        // iOS/Android may already have revoked this token. Clear our local
        // association in either case so a future enable action starts cleanly.
      }
    }
    await Preferences.remove({ key: tokenKey(platform) });
  } catch (error) {
    throw new Error(error?.message || "Could not disable native push notifications.");
  }
  return getNativePushState();
}

export async function clearNativePushOnSignOut() {
  if (!nativePlatform()) return;
  try {
    await unsubscribeFromNativePush();
  } catch {
    // Signing out must remain available even if device cleanup is offline.
  }
}

export async function wireNativeNotificationActions() {
  const platform = nativePlatform();
  if (!platform) return;
  try {
    const { PushNotifications } = await nativePlugins();
    await PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      const destination = action?.notification?.data?.url;
      if (typeof destination === "string" && destination.startsWith("/") && !destination.startsWith("//")) {
        window.location.assign(destination);
      }
    });
  } catch {
    // The preference screen provides a recoverable setup state if the plugin
    // is absent or native configuration has not been completed yet.
  }
}
